/**
 * Sample closures, placed relative to today so "last Tuesday" is always a
 * real day with a real closure on it. The rubber removal on the runway is
 * the one the friction alert raised WO-000006 for, so the question "why did
 * we close runway 17/35 last Tuesday" has an inspection, a work order,
 * photographs and a clause to answer with.
 */
import type { Closure } from "../domain/closures";

const woId = (n: number) => `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;

/** The most recent Tuesday strictly before today — today being Tuesday means a week ago. */
export function lastWeekday(now: Date, weekday: number): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const back = ((d.getDay() - weekday + 7) % 7) || 7;
  d.setDate(d.getDate() - back);
  return d;
}

function at(day: Date, hour: number, minute: number): string {
  const d = new Date(day);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

/**
 * The surfaces the sample aerodrome can close, by the code the platform knows
 * them by: a runway pair (RWY-18-36), a taxiway the map offers (TWY-J1), a
 * stand. Anything else is refused as the platform refuses a zone the site
 * does not have.
 */
export const isMockSurface = (code: string): boolean =>
  /^RWY-\d{2}[LRC]?-\d{2}[LRC]?$|^TWY-[A-Z]{1,2}\d{0,2}$|^STAND-[A-Z0-9]+$/.test(code.trim());

export function mockClosures(now = new Date()): Closure[] {
  const tue = lastWeekday(now, 2);
  const twelveDaysAgo = new Date(now.getTime() - 12 * 86_400_000);
  const threeDaysAgo = new Date(now.getTime() - 3 * 86_400_000);
  return [
    {
      id: "cls-0142",
      reference: "CLS-0142",
      kind: "unplanned",
      extent: "full",
      reason: "Friction below minimum, CH 1200–1500 — rubber removal in the touchdown zone",
      reasonCode: "friction_below_minimum",
      status: "ended",
      startedAt: at(tue, 6, 10),
      endedAt: at(tue, 12, 22),
      fromChainageM: 1200,
      toChainageM: 1500,
      zoneCode: "RWY-17-35",
      workOrder: { id: woId(6), number: "WO-000006", title: "Rubber removal — RWY 17 touchdown zone" },
      minutes: 372,
    },
    {
      id: "cls-0143",
      reference: "CLS-0143",
      kind: "planned",
      extent: "partial",
      reason: "AGL circuit HP-B isolated for lamp replacement",
      status: "ended",
      startedAt: at(tue, 22, 0),
      endedAt: at(tue, 23, 30),
      zoneCode: "TWY-B",
      workOrder: { id: woId(5), number: "WO-000005", title: "Light unit failure — holding position B1" },
      minutes: 90,
    },
    {
      id: "cls-0139",
      reference: "CLS-0139",
      kind: "planned",
      extent: "full",
      reason: "Marking repaint — RWY 35 threshold",
      status: "ended",
      startedAt: at(twelveDaysAgo, 1, 0),
      endedAt: at(twelveDaysAgo, 4, 0),
      zoneCode: "RWY-17-35",
      workOrder: { id: woId(7), number: "WO-000007", title: "Marking repaint — RWY 35 threshold" },
      minutes: 180,
    },
    {
      id: "cls-0141",
      reference: "CLS-0141",
      kind: "unplanned",
      extent: "partial",
      reason: "Boarding bridge PBB-A12 failed with an aircraft on stand",
      status: "ended",
      startedAt: at(threeDaysAgo, 8, 40),
      endedAt: at(threeDaysAgo, 9, 40),
      zoneCode: "STAND-A12",
      minutes: 60,
    },
  ];
}
